import React, { useEffect, useState, useCallback } from "react";
import api from "../../api/client";
import Header from "../../components/layout/Header";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Save, KeyRound, CheckCircle2 } from "lucide-react";
import CollaboratorsTab from "./CollaboratorsTab";
import ActivityLogsTab from "./ActivityLogsTab";
import TOTPSection from "./TOTPSection";
import IntegrationsConfigTab from "./IntegrationsConfigTab";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("consultancy");
  const [newKey, setNewKey] = useState("");
  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [totpState, setTotpState] = useState({ enabled: false, remaining: 0 });

  const loadTotp = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setTotpState({ enabled: !!r.data.totp_enabled, remaining: r.data.totp_recovery_remaining || 0 });
    } catch (e) { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try { setSettings((await api.get("/settings")).data); }
    catch (e) { toast.error("Falha ao carregar configurações"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); loadTotp(); }, [load, loadTotp]);

  const updCons = (k, v) => setSettings({ ...settings, consultancy: { ...settings.consultancy, [k]: v } });
  const updClaude = (k, v) => setSettings({ ...settings, claude: { ...settings.claude, [k]: v } });
  const updProp = (k, v) => setSettings({ ...settings, proposal: { ...settings.proposal, [k]: v } });

  const saveCons = async () => {
    try { await api.put("/settings/consultancy", { consultancy: settings.consultancy }); toast.success("Salvo"); }
    catch (e) { toast.error("Falha"); }
  };
  const saveClaudeConfig = async () => {
    try { await api.put("/settings/claude-config", { modelo: settings.claude.modelo, max_tokens: settings.claude.max_tokens, temperature: settings.claude.temperature }); toast.success("Salvo"); }
    catch (e) { toast.error("Falha"); }
  };
  const saveProp = async () => {
    try { await api.put("/settings/proposal-config", {
      margem_alerta_pct: settings.proposal.margem_alerta_pct,
      markup_quimico_default: settings.proposal.markup_quimico_default,
      valor_por_funcionario_default: settings.proposal.valor_por_funcionario_default || 0,
      custo_acompanhamento_funcionario_default: settings.proposal.custo_acompanhamento_funcionario_default || 0,
      valor_por_visita_default: settings.proposal.valor_por_visita_default || 0,
      custo_visita_default: settings.proposal.custo_visita_default || 0,
      custo_deslocamento_km: settings.proposal.custo_deslocamento_km || 0,
      depreciacao_geral_pct: settings.proposal.depreciacao_geral_pct || 0,
    }); toast.success("Salvo"); }
    catch (e) { toast.error("Falha"); }
  };
  const saveKey = async () => {
    if (!newKey || !newKey.startsWith("sk-ant-")) return toast.error("Chave Anthropic deve começar com sk-ant-");
    try { await api.put("/settings/claude-key", { api_key: newKey }); toast.success("Chave salva criptografada"); setNewKey(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };
  const testKey = async () => {
    try { const r = await api.post("/settings/test-claude"); toast.success(`✓ Chave válida (modelo: ${r.data.model_used})`); }
    catch (e) { toast.error(e.response?.data?.detail || "Chave inválida"); }
  };

  const changePassword = async () => {
    if (pwdForm.new_password.length < 12) return toast.error("Nova senha mínima 12 chars");
    if (pwdForm.new_password !== pwdForm.confirm) return toast.error("Senhas não conferem");
    try {
      await api.put("/auth/change-password", { current_password: pwdForm.current_password, new_password: pwdForm.new_password });
      toast.success("Senha alterada");
      setPwdForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };

  if (loading || !settings) return <><Header title="Configurações" /><div className="p-8 text-muted-foreground">Carregando...</div></>;

  return (
    <>
      <Header title="Configurações" />
      <div className="p-8 max-w-5xl" data-testid="settings-page">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="settings-tabs" className="flex flex-wrap h-auto">
            <TabsTrigger value="consultancy" data-testid="tab-consultancy">Consultoria</TabsTrigger>
            <TabsTrigger value="claude" data-testid="tab-claude">Assistente de IA</TabsTrigger>
            <TabsTrigger value="integrations" data-testid="tab-integrations">APIs e Integrações</TabsTrigger>
            <TabsTrigger value="proposal" data-testid="tab-proposal">Propostas</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">Segurança</TabsTrigger>
            <TabsTrigger value="collaborators" data-testid="tab-collaborators">Colaboradores</TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-activity-logs">Histórico de atividades</TabsTrigger>
          </TabsList>

          <TabsContent value="consultancy" className="space-y-4 pt-6">
            <h3 className="label-tech">Dados da Consultoria</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label className="label-tech">Razão Social</Label><Input value={settings.consultancy.razao_social} onChange={(e) => updCons("razao_social", e.target.value)} data-testid="cons-razao-input" /></div>
              <div><Label className="label-tech">Nome Fantasia</Label><Input value={settings.consultancy.nome_fantasia} onChange={(e) => updCons("nome_fantasia", e.target.value)} data-testid="cons-fantasia-input" /></div>
              <div><Label className="label-tech">CNPJ</Label><Input value={settings.consultancy.cnpj} onChange={(e) => updCons("cnpj", e.target.value)} data-testid="cons-cnpj-input" /></div>
              <div><Label className="label-tech">Endereço</Label><Input value={settings.consultancy.endereco} onChange={(e) => updCons("endereco", e.target.value)} data-testid="cons-endereco-input" /></div>
              <div><Label className="label-tech">Município</Label><Input value={settings.consultancy.municipio} onChange={(e) => updCons("municipio", e.target.value)} data-testid="cons-municipio-input" /></div>
              <div><Label className="label-tech">UF</Label><Input value={settings.consultancy.uf} onChange={(e) => updCons("uf", e.target.value)} data-testid="cons-uf-input" /></div>
              <div><Label className="label-tech">Responsável Técnico</Label><Input value={settings.consultancy.responsavel_tecnico} onChange={(e) => updCons("responsavel_tecnico", e.target.value)} data-testid="cons-rt-input" /></div>
              <div><Label className="label-tech">CREA</Label><Input value={settings.consultancy.crea} onChange={(e) => updCons("crea", e.target.value)} data-testid="cons-crea-input" /></div>
              <div><Label className="label-tech">Telefone</Label><Input value={settings.consultancy.telefone} onChange={(e) => updCons("telefone", e.target.value)} data-testid="cons-tel-input" /></div>
              <div><Label className="label-tech">E-mail</Label><Input value={settings.consultancy.email} onChange={(e) => updCons("email", e.target.value)} data-testid="cons-email-input" /></div>
            </div>
            <Button onClick={saveCons} data-testid="save-consultancy-btn"><Save className="size-4 mr-1" /> Salvar</Button>
          </TabsContent>

          <TabsContent value="claude" className="space-y-6 pt-6">
            <section className="space-y-4">
              <h3 className="label-tech">Chave da API Anthropic</h3>
              <div className="bg-muted/30 border border-border rounded p-4 flex items-center gap-3" data-testid="claude-key-status">
                <KeyRound className="size-4 text-muted-foreground" />
                <div className="flex-1">
                  <div className="text-sm font-mono">{settings.claude.has_key ? settings.claude.key_masked : "Nenhuma chave cadastrada"}</div>
                </div>
                {settings.claude.has_key && (
                  <Button variant="outline" size="sm" onClick={testKey} data-testid="test-claude-btn"><CheckCircle2 className="size-4 mr-1" /> Validar</Button>
                )}
              </div>
              <div className="grid md:grid-cols-[1fr_auto] gap-2">
                <Input type="password" placeholder="sk-ant-api03-..." value={newKey} onChange={(e) => setNewKey(e.target.value)} data-testid="new-claude-key-input" />
                <Button onClick={saveKey} data-testid="save-claude-key-btn">Salvar chave</Button>
              </div>
            </section>

            <section className="space-y-4">
              <h3 className="label-tech">Modelo & Parâmetros</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="label-tech">Modelo padrão</Label>
                  <Select value={settings.claude.modelo} onValueChange={(v) => updClaude("modelo", v)}>
                    <SelectTrigger data-testid="claude-model-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="claude-opus-4-5-20251101">claude-opus-4-5</SelectItem>
                      <SelectItem value="claude-sonnet-4-5-20250929">claude-sonnet-4-5</SelectItem>
                      <SelectItem value="claude-haiku-4-5-20251001">claude-haiku-4-5</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="label-tech">max_tokens <span className="text-xs text-muted-foreground">(1024–64000)</span></Label><Input type="number" min="1024" max="64000" value={settings.claude.max_tokens} onChange={(e) => updClaude("max_tokens", Math.min(64000, Math.max(1024, parseInt(e.target.value || 32000, 10))))} data-testid="claude-max-tokens-input" /></div>
                <div><Label className="label-tech">temperature</Label><Input type="number" step="0.05" min="0" max="1" value={settings.claude.temperature} onChange={(e) => updClaude("temperature", parseFloat(e.target.value || 0.3))} data-testid="claude-temp-input" /></div>
              </div>
              <Button onClick={saveClaudeConfig} data-testid="save-claude-config-btn"><Save className="size-4 mr-1" /> Salvar</Button>
            </section>
          </TabsContent>

          <TabsContent value="integrations" className="pt-6">
            <IntegrationsConfigTab config={settings.integrations} onSaved={load} />
          </TabsContent>

          <TabsContent value="proposal" className="space-y-6 pt-6">
            <section className="space-y-4">
              <h3 className="label-tech">Limiares de propostas</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label className="label-tech">Alerta margem mínima (%)</Label><Input type="number" step="0.5" value={settings.proposal.margem_alerta_pct} onChange={(e) => updProp("margem_alerta_pct", parseFloat(e.target.value || 20))} data-testid="margem-alerta-input" /></div>
                <div><Label className="label-tech">Multiplicador padrão químico</Label><Input type="number" step="0.01" value={settings.proposal.markup_quimico_default} onChange={(e) => updProp("markup_quimico_default", parseFloat(e.target.value || 1.75))} data-testid="markup-quim-input" /></div>
              </div>
            </section>

            <section className="space-y-4 border-t border-border pt-6">
              <div>
                <h3 className="label-tech">Acompanhamento — valores e custos padrão</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Aplicados automaticamente a TODAS as propostas (inclusive as geradas por colaboradores).
                  Podem ser sobrescritos manualmente pelo admin em cada proposta.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="label-tech">Valor por funcionário/mês (R$)</Label>
                  <Input type="number" step="0.01" value={settings.proposal.valor_por_funcionario_default || 0} onChange={(e) => updProp("valor_por_funcionario_default", parseFloat(e.target.value || 0))} data-testid="valor-func-default-input" />
                </div>
                <div>
                  <Label className="label-tech">Custo interno por func/mês (R$)</Label>
                  <Input type="number" step="0.01" value={settings.proposal.custo_acompanhamento_funcionario_default || 0} onChange={(e) => updProp("custo_acompanhamento_funcionario_default", parseFloat(e.target.value || 0))} data-testid="custo-func-default-input" />
                </div>
                <div>
                  <Label className="label-tech">Valor por visita técnica (R$)</Label>
                  <Input type="number" step="0.01" value={settings.proposal.valor_por_visita_default || 0} onChange={(e) => updProp("valor_por_visita_default", parseFloat(e.target.value || 0))} data-testid="valor-visita-default-input" />
                </div>
                <div>
                  <Label className="label-tech">Custo interno por visita (R$)</Label>
                  <Input type="number" step="0.01" value={settings.proposal.custo_visita_default || 0} onChange={(e) => updProp("custo_visita_default", parseFloat(e.target.value || 0))} data-testid="custo-visita-default-input" />
                </div>
              </div>
            </section>

            <section className="space-y-4 border-t border-border pt-6">
              <div>
                <h3 className="label-tech">Deslocamento e depreciação (ocultos do cliente)</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Valores aplicados internamente no cálculo final da proposta. Não aparecem no DOCX nem no
                  resumo exibido ao cliente. O deslocamento só incide quando a cidade do cliente é diferente
                  de <span className="font-mono text-foreground">Nova Friburgo</span>; a fórmula é
                  <span className="font-mono text-foreground"> custo_km × 2 × distância × nº visitas</span>.
                  A depreciação é um reajuste percentual aplicado sobre o valor final
                  (ex.: 2% → valor × 1,02).
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="label-tech">Custo de deslocamento (R$/km)</Label>
                  <Input type="number" step="0.01" min="0" value={settings.proposal.custo_deslocamento_km || 0} onChange={(e) => updProp("custo_deslocamento_km", parseFloat(e.target.value || 0))} data-testid="custo-deslocamento-km-input" />
                </div>
                <div>
                  <Label className="label-tech">Depreciação geral (%)</Label>
                  <Input type="number" step="0.01" min="0" value={settings.proposal.depreciacao_geral_pct || 0} onChange={(e) => updProp("depreciacao_geral_pct", parseFloat(e.target.value || 0))} data-testid="depreciacao-geral-input" />
                </div>
              </div>
            </section>
            <Button onClick={saveProp} data-testid="save-proposal-config-btn"><Save className="size-4 mr-1" /> Salvar</Button>
          </TabsContent>

          <TabsContent value="security" className="space-y-8 pt-6">
            <section className="space-y-4">
              <h3 className="label-tech">Autenticação em 2 etapas (Google Authenticator)</h3>
              <TOTPSection
                enabled={totpState.enabled}
                recoveryRemaining={totpState.remaining}
                onChange={loadTotp}
              />
            </section>

            <section className="space-y-4 border-t border-border pt-6">
              <h3 className="label-tech">Trocar Senha de Acesso</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <Input type="password" placeholder="Senha atual" value={pwdForm.current_password} onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })} data-testid="pwd-current-input" />
                <Input type="password" placeholder="Nova senha" value={pwdForm.new_password} onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })} data-testid="pwd-new-input" />
                <Input type="password" placeholder="Confirmar nova" value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} data-testid="pwd-confirm-input" />
              </div>
              <Button onClick={changePassword} data-testid="change-password-btn">Alterar senha</Button>
            </section>
          </TabsContent>

          <TabsContent value="collaborators" className="space-y-4 pt-6">
            <CollaboratorsTab />
          </TabsContent>

          <TabsContent value="logs" className="space-y-4 pt-6">
            <ActivityLogsTab />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

